import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { worklogApi } from "./api";
import type { DashboardData, Person, Project, Role, Session, Task, TaskPriority, WorkLog, WorkLogPayload } from "./types";

type View = "dashboard" | "board" | "log" | "report" | "salary" | "settings";
type ToastState = { message: string; tone: "success" | "error" } | null;
type IconName = "dashboard" | "board" | "clock" | "report" | "salary" | "settings" | "plus" | "edit" | "trash";

function localIsoDate(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function localMonth(): string {
  return localIsoDate().slice(0, 7);
}

function formatMonth(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(
    new Date(year, monthNumber - 1, 1),
  );
}

function formatDate(date: string): string {
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(
    new Date(`${date}T00:00:00`),
  );
}

function formatHours(minutes: number): string {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(2).replace(/0$/, "")}h`;
}

function formatRupiah(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value);
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    dashboard: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    board: <><rect x="4" y="5" width="16" height="14" rx="2" /><path d="M9 5v14M15 5v14" /></>,
    clock: <><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></>,
    report: <><path d="M5 4h14v16H5z" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
    salary: <><path d="M4 7h16v10H4z" /><path d="M8 12h8M12 9v6" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    edit: <><path d="m4 20 4.5-1 10-10-3.5-3.5-10 10z" /><path d="m13.5 7 3.5 3.5" /></>,
    trash: <><path d="M5 7h14M9 7V4h6v3M8 10v8M12 10v8M16 10v8M7 7l1 14h8l1-14" /></>,
  };
  return <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function MonthPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="month-picker">
      <span>Month</span>
      <input type="month" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Toast({ toast }: { toast: ToastState }) {
  if (!toast) return null;
  return <div className={`toast toast-${toast.tone}`}>{toast.message}</div>;
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <div className="empty-state">{children}</div>;
}

type WorkFormProps = {
  projects: Project[];
  tasks: Task[];
  editing: WorkLog | null;
  onCancelEdit: () => void;
  onSaved: () => Promise<void>;
  showToast: (message: string, tone?: "success" | "error") => void;
};

function WorkForm({ projects, tasks, editing, onCancelEdit, onSaved, showToast }: WorkFormProps) {
  const activeTasks = tasks.filter((task) => projects.some((project) => project.id === task.projectId && project.active));
  const [workDate, setWorkDate] = useState(localIsoDate());
  const [taskId, setTaskId] = useState(activeTasks[0]?.id ?? "");
  const [hours, setHours] = useState("1");
  const [billable, setBillable] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setWorkDate(editing.workDate);
      setTaskId(editing.taskId ?? "");
      setHours(String(editing.hoursMinutes / 60));
      setBillable(editing.billable);
      setNote(editing.note ?? "");
    }
  }, [editing]);

  useEffect(() => {
    if (!taskId && activeTasks[0]) setTaskId(activeTasks[0].id);
  }, [activeTasks, taskId]);

  function reset() {
    setWorkDate(localIsoDate());
    setTaskId(activeTasks[0]?.id ?? "");
    setHours("1");
    setBillable(true);
    setNote("");
    onCancelEdit();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const hoursNumber = Number(hours);
    const hoursMinutes = Math.round(hoursNumber * 60);
    if (!taskId || !Number.isFinite(hoursNumber) || hoursMinutes < 1) {
      showToast("Please choose a task and enter the hours.", "error");
      return;
    }

    const payload: WorkLogPayload = {
      taskId,
      workDate,
      hoursMinutes,
      billable,
      note: note.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) await worklogApi.updateLog(editing.id, payload);
      else await worklogApi.createLog(payload);
      await onSaved();
      showToast(editing ? "Work entry updated." : "Work entry added.");
      reset();
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Could not save the entry.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="work-form card" onSubmit={submit}>
      <div className="card-heading">
        <div>
          <p className="eyebrow">{editing ? "Editing entry" : "Quick entry"}</p>
          <h2>{editing ? "Update work" : "What did you work on?"}</h2>
        </div>
        {editing && <button className="text-button" type="button" onClick={reset}>Cancel</button>}
      </div>
      <div className="form-grid">
        <label>
          <span>Date</span>
          <input type="date" value={workDate} onChange={(event) => setWorkDate(event.target.value)} required />
        </label>
        <label className="field-task-picker">
          <span>Assigned task</span>
          <select value={taskId} onChange={(event) => setTaskId(event.target.value)} required>
            {activeTasks.map((task) => {
              const project = projects.find((item) => item.id === task.projectId);
              return <option key={task.id} value={task.id}>{project?.name ?? "Project"} · {task.title}</option>;
            })}
          </select>
        </label>
        <label>
          <span>Hours</span>
          <input type="number" min="0.25" max="24" step="0.25" value={hours} onChange={(event) => setHours(event.target.value)} required />
        </label>
        <label className="field-note">
          <span>Note <small>optional</small></span>
          <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="A short detail for the monthly report" maxLength={1000} />
        </label>
        <label className="billable-toggle">
          <input type="checkbox" checked={billable} onChange={(event) => setBillable(event.target.checked)} />
          <span className="toggle-track"><span /></span>
          <span>Billable</span>
        </label>
      </div>
      <div className="form-actions">
        <span className="form-hint">Use quarter-hour increments when practical.</span>
        <button className="primary-button" disabled={saving || activeTasks.length === 0}>
          <Icon name={editing ? "edit" : "plus"} />
          {saving ? "Saving…" : editing ? "Save changes" : "Add work"}
        </button>
      </div>
    </form>
  );
}

type WorkListProps = {
  logs: WorkLog[];
  currentPersonId: string;
  isAdmin: boolean;
  onEdit: (log: WorkLog) => void;
  onDeleted: () => Promise<void>;
  showToast: (message: string, tone?: "success" | "error") => void;
};

function WorkList({ logs, currentPersonId, isAdmin, onEdit, onDeleted, showToast }: WorkListProps) {
  async function remove(log: WorkLog) {
    if (!window.confirm(`Delete “${log.task}”?`)) return;
    try {
      await worklogApi.deleteLog(log.id);
      await onDeleted();
      showToast("Work entry deleted.");
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Could not delete the entry.", "error");
    }
  }

  if (logs.length === 0) return <EmptyState>No work entries for this month yet.</EmptyState>;

  return (
    <div className="work-list card">
      <div className="card-heading">
        <div>
          <p className="eyebrow">This month</p>
          <h2>Recorded work</h2>
        </div>
        <span className="record-count">{logs.length} {logs.length === 1 ? "entry" : "entries"}</span>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Task</th><th>Project</th>{isAdmin && <th>Person</th>}<th className="number-cell">Hours</th><th>Status</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {logs.map((log) => {
              const canEdit = isAdmin || log.personId === currentPersonId;
              return (
                <tr key={log.id}>
                  <td className="date-cell">{formatDate(log.workDate)}</td>
                  <td><strong>{log.task}</strong>{log.note && <small className="table-note">{log.note}</small>}</td>
                  <td><span className="project-name">{log.projectName}</span><small>{log.clientName}</small></td>
                  {isAdmin && <td><span className="person-cell"><span className="mini-avatar">{initials(log.personName)}</span>{log.personName}</span></td>}
                  <td className="number-cell"><strong>{formatHours(log.hoursMinutes)}</strong></td>
                  <td><span className={`status-pill ${log.billable ? "billable" : "non-billable"}`}>{log.billable ? "Billable" : "Internal"}</span></td>
                  <td className="row-actions">{canEdit && <><button title="Edit entry" onClick={() => onEdit(log)}><Icon name="edit" /></button><button title="Delete entry" onClick={() => remove(log)}><Icon name="trash" /></button></>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCards({ logs }: { logs: WorkLog[] }) {
  const totalMinutes = logs.reduce((sum, log) => sum + log.hoursMinutes, 0);
  const billableMinutes = logs.filter((log) => log.billable).reduce((sum, log) => sum + log.hoursMinutes, 0);
  const projectCount = new Set(logs.map((log) => log.projectId)).size;
  return (
    <div className="summary-grid">
      <div className="summary-card"><span>Total recorded</span><strong>{formatHours(totalMinutes)}</strong><small>all work this month</small></div>
      <div className="summary-card accent"><span>Billable</span><strong>{formatHours(billableMinutes)}</strong><small>{totalMinutes ? Math.round((billableMinutes / totalMinutes) * 100) : 0}% of recorded time</small></div>
      <div className="summary-card"><span>Projects touched</span><strong>{projectCount}</strong><small>across {logs.length} entries</small></div>
    </div>
  );
}

function AdminDashboard({ month }: { month: string }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    worklogApi.dashboard(month)
      .then((result) => { if (!cancelled) { setData(result); setLoadError(""); } })
      .catch((caught) => { if (!cancelled) setLoadError(caught instanceof Error ? caught.message : "Could not load the dashboard."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  if (loading && !data) return <div className="dashboard-loading card">Loading dashboard…</div>;
  if (loadError || !data) return <EmptyState>{loadError || "Dashboard data is unavailable."}</EmptyState>;

  const attentionCount = data.overdueTaskCount + data.unassignedTaskCount;
  return (
    <section>
      <div className="page-title-row">
        <div><p className="eyebrow">Admin overview · {formatMonth(month)}</p><h1>Dashboard</h1><p>Monthly hours, project activity, team contribution, and tasks that need attention.</p></div>
      </div>

      <div className="dashboard-kpi-grid">
        <div className="dashboard-kpi card"><span>Total recorded</span><strong>{formatHours(data.loggedMinutes)}</strong><small>{data.activePeople} active {data.activePeople === 1 ? "person" : "people"}</small></div>
        <div className="dashboard-kpi card accent"><span>Billable hours</span><strong>{formatHours(data.billableMinutes)}</strong><small>{formatHours(data.nonBillableMinutes)} internal / non-billable</small></div>
        <div className="dashboard-kpi card"><span>Task progress</span><strong>{data.doneTaskCount}/{data.taskCount}</strong><small>currently marked done</small></div>
        <div className={`dashboard-kpi card ${attentionCount ? "warning" : ""}`}><span>Needs attention</span><strong>{attentionCount}</strong><small>{data.overdueTaskCount} overdue · {data.unassignedTaskCount} unassigned</small></div>
      </div>

      <div className="dashboard-grid">
        <article className="dashboard-panel card">
          <div className="card-heading"><div><p className="eyebrow">By project</p><h2>Project activity</h2></div><span className="record-count">{data.activeProjects} active this month</span></div>
          <div className="dashboard-list">{data.projects.map((project) => {
            const progress = project.taskCount ? Math.round((project.doneTaskCount / project.taskCount) * 100) : 0;
            return <div className="dashboard-list-row" key={project.projectId}><div><strong>{project.projectName}</strong><small>{project.clientName}</small></div><div className="project-progress"><span><i style={{ width: `${progress}%` }} /></span><small>{project.doneTaskCount}/{project.taskCount} tasks</small></div><div className="dashboard-value"><strong>{formatHours(project.loggedMinutes)}</strong><small>{formatHours(project.billableMinutes)} billable</small></div></div>;
          })}</div>
        </article>

        <article className="dashboard-panel card">
          <div className="card-heading"><div><p className="eyebrow">By person</p><h2>Team hours</h2></div></div>
          {data.people.length ? <div className="dashboard-list">{data.people.map((person) => <div className="dashboard-list-row person-summary" key={person.personId}><span className="avatar">{initials(person.personName)}</span><div><strong>{person.personName}</strong><small>{formatHours(person.billableMinutes)} billable</small></div><div className="dashboard-value"><strong>{formatHours(person.loggedMinutes)}</strong><small>recorded</small></div></div>)}</div> : <EmptyState>No hours recorded for this month.</EmptyState>}
        </article>
      </div>

      <article className="dashboard-panel dashboard-recent card">
        <div className="card-heading"><div><p className="eyebrow">Latest entries</p><h2>Recent work</h2></div></div>
        {data.recentLogs.length ? <div className="dashboard-list">{data.recentLogs.map((log) => <div className="dashboard-list-row recent-log" key={log.id}><span className="date-chip">{formatDate(log.workDate)}</span><div><strong>{log.task}</strong><small>{log.personName} · {log.clientName} / {log.projectName}</small></div><div className="dashboard-value"><strong>{formatHours(log.hoursMinutes)}</strong><small>{log.billable ? "Billable" : "Internal"}</small></div></div>)}</div> : <EmptyState>No work entries for {formatMonth(month)} yet.</EmptyState>}
      </article>
    </section>
  );
}

type BoardsProps = {
  session: Session;
  refresh: () => Promise<void>;
  showToast: (message: string, tone?: "success" | "error") => void;
};

function Boards({ session, refresh, showToast }: BoardsProps) {
  const activeProjects = session.projects.filter((project) => project.active);
  const [projectId, setProjectId] = useState(activeProjects[0]?.id ?? "");
  const projectBoards = session.boards.filter((board) => board.projectId === projectId && board.active);
  const [boardId, setBoardId] = useState(projectBoards[0]?.id ?? "");
  const [boardName, setBoardName] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const isAdmin = session.me.role === "admin";

  useEffect(() => {
    if (!activeProjects.some((project) => project.id === projectId)) {
      setProjectId(activeProjects[0]?.id ?? "");
    }
  }, [activeProjects, projectId]);

  useEffect(() => {
    const nextBoards = session.boards.filter((board) => board.projectId === projectId && board.active);
    if (!nextBoards.some((board) => board.id === boardId)) setBoardId(nextBoards[0]?.id ?? "");
  }, [boardId, projectId, session.boards]);

  const columns = session.columns
    .filter((column) => column.boardId === boardId)
    .sort((a, b) => a.position - b.position);
  const tasks = session.tasks.filter((task) => task.boardId === boardId);
  const memberIds = new Set(session.projectMembers.filter((item) => item.projectId === projectId).map((item) => item.personId));
  const members = session.people.filter((person) => person.active && memberIds.has(person.id));

  async function createBoard(event: FormEvent) {
    event.preventDefault();
    if (!projectId || !boardName.trim()) return;
    setSaving(true);
    try {
      await worklogApi.createBoard({ projectId, name: boardName.trim() });
      setBoardName("");
      await refresh();
      showToast("Board created.");
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Could not create the board.", "error");
    } finally { setSaving(false); }
  }

  async function createTask(event: FormEvent) {
    event.preventDefault();
    if (!projectId || !boardId || !taskTitle.trim()) return;
    setSaving(true);
    try {
      await worklogApi.createTask({
        projectId,
        boardId,
        title: taskTitle.trim(),
        description: taskDescription.trim() || null,
        assigneeId: assigneeId || null,
        priority,
        dueDate: dueDate || null,
      });
      setTaskTitle(""); setTaskDescription(""); setAssigneeId(""); setPriority("medium"); setDueDate("");
      await refresh();
      showToast("Task created.");
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Could not create the task.", "error");
    } finally { setSaving(false); }
  }

  async function move(task: Task, columnId: string) {
    try {
      await worklogApi.moveTask(task.id, columnId);
      await refresh();
      showToast("Task status updated.");
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Could not move the task.", "error");
    }
  }

  return (
    <section>
      <div className="page-title-row board-title-row">
        <div><p className="eyebrow">Task tracking</p><h1>Boards</h1><p>Assign the work first, then let the assignee record time against it.</p></div>
        <div className="board-filters">
          <label><span>Project</span><select value={projectId} onChange={(event) => setProjectId(event.target.value)}>{activeProjects.map((project) => <option key={project.id} value={project.id}>{project.clientName} · {project.name}</option>)}</select></label>
          <label><span>Board</span><select value={boardId} onChange={(event) => setBoardId(event.target.value)}>{projectBoards.map((board) => <option key={board.id} value={board.id}>{board.name}</option>)}</select></label>
        </div>
      </div>

      {isAdmin && <div className="board-admin-grid">
        <form className="card board-create-form" onSubmit={createBoard}>
          <div className="admin-form-heading"><p className="eyebrow">New board</p><h2>Another workflow</h2><p>Use another board only when the project needs a separate workflow.</p></div>
          <label><span>Board name</span><input value={boardName} onChange={(event) => setBoardName(event.target.value)} placeholder="e.g. Infrastructure" maxLength={120} required /></label>
          <button className="secondary-button" disabled={saving || !projectId}><Icon name="plus" />Create board</button>
        </form>
        <form className="card task-create-form" onSubmit={createTask}>
          <div className="admin-form-heading"><p className="eyebrow">New task</p><h2>Assign work</h2><p>Create a clear task, choose its owner, then track it on the board.</p></div>
          <label className="task-title-field"><span>Task title</span><input value={taskTitle} onChange={(event) => setTaskTitle(event.target.value)} placeholder="What needs to be done?" maxLength={240} required /></label>
          <label className="task-description-field"><span>Description <small>optional</small></span><input value={taskDescription} onChange={(event) => setTaskDescription(event.target.value)} placeholder="Add a short detail" maxLength={2000} /></label>
          <label><span>Assignee</span><select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}><option value="">Unassigned</option>{members.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
          <label><span>Priority</span><select value={priority} onChange={(event) => setPriority(event.target.value as TaskPriority)}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
          <label><span>Due date <small>optional</small></span><input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></label>
          <button className="primary-button task-submit" disabled={saving || !boardId}><Icon name="plus" />Create task</button>
        </form>
      </div>}

      {!boardId ? <EmptyState>{isAdmin ? "Create a board for this project." : "No board is available for this project."}</EmptyState> :
        <div className="kanban-board">{columns.map((column) => {
          const columnTasks = tasks.filter((task) => task.columnId === column.id);
          return <div className="kanban-column" key={column.id}><header><strong>{column.name}</strong><span>{columnTasks.length}</span></header><div className="kanban-stack">{columnTasks.map((task) => {
            const canMove = isAdmin || task.assigneeId === session.me.id;
            return <article className="task-card" key={task.id}><div className="task-meta"><span className={`priority priority-${task.priority}`}>{task.priority}</span>{task.dueDate && <span>Due {formatDate(task.dueDate)}</span>}</div><h3>{task.title}</h3>{task.description && <p>{task.description}</p>}<div className="task-footer"><span>{task.assigneeName ? <><span className="mini-avatar">{initials(task.assigneeName)}</span>{task.assigneeName}</> : "Unassigned"}</span>{canMove && <select aria-label={`Status for ${task.title}`} value={task.columnId} onChange={(event) => void move(task, event.target.value)}>{columns.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>}</div></article>;
          })}{columnTasks.length === 0 && <div className="column-empty">No tasks</div>}</div></div>;
        })}</div>}
    </section>
  );
}

function MonthlyReport({ logs, projects, month }: { logs: WorkLog[]; projects: Project[]; month: string }) {
  const activeProjects = projects.filter((project) => project.active || logs.some((log) => log.projectId === project.id));
  const [projectId, setProjectId] = useState(activeProjects[0]?.id ?? "");

  useEffect(() => {
    if (!activeProjects.some((project) => project.id === projectId)) setProjectId(activeProjects[0]?.id ?? "");
  }, [activeProjects, projectId]);

  const project = projects.find((item) => item.id === projectId);
  const projectLogs = logs.filter((log) => log.projectId === projectId).sort((a, b) => a.workDate.localeCompare(b.workDate));
  const totalMinutes = projectLogs.reduce((sum, log) => sum + log.hoursMinutes, 0);
  const billableMinutes = projectLogs.filter((log) => log.billable).reduce((sum, log) => sum + log.hoursMinutes, 0);

  return (
    <section>
      <div className="page-title-row no-print">
        <div><p className="eyebrow">Monthly output</p><h1>Project report</h1><p>Select a project, review the work, then print or save it as PDF.</p></div>
        <div className="report-actions">
          <label><span>Project</span><select value={projectId} onChange={(event) => setProjectId(event.target.value)}>{activeProjects.map((item) => <option key={item.id} value={item.id}>{item.clientName} · {item.name}</option>)}</select></label>
          <button className="secondary-button" onClick={() => window.print()}><Icon name="report" /> Print / PDF</button>
        </div>
      </div>

      <article className="report-sheet">
        <header className="report-header">
          <div className="brand-lockup"><span className="brand-mark small">C1</span><span>Cloud1</span></div>
          <div><span>Monthly work report</span><strong>{formatMonth(month)}</strong></div>
        </header>
        {project ? (
          <>
            <div className="report-project"><p>Prepared for</p><h2>{project.clientName}</h2><span>{project.name}</span></div>
            <div className="report-totals"><div><span>Total work</span><strong>{formatHours(totalMinutes)}</strong></div><div><span>Billable work</span><strong>{formatHours(billableMinutes)}</strong></div><div><span>Activities</span><strong>{projectLogs.length}</strong></div></div>
            <div className="report-section"><h3>Work completed</h3>{projectLogs.length ? <table className="report-table"><thead><tr><th>Date</th><th>Activity</th><th>Person</th><th>Hours</th></tr></thead><tbody>{projectLogs.map((log) => <tr key={log.id}><td>{formatDate(log.workDate)}</td><td><strong>{log.task}</strong>{log.note && <small>{log.note}</small>}</td><td>{log.personName}</td><td>{formatHours(log.hoursMinutes)}</td></tr>)}</tbody></table> : <EmptyState>No entries for this project in {formatMonth(month)}.</EmptyState>}</div>
            <footer className="report-footer"><span>PT Cloud One Global</span><span>Generated from Cloud1 Work Log</span></footer>
          </>
        ) : <EmptyState>Add a project before creating a report.</EmptyState>}
      </article>
    </section>
  );
}

function SalaryReport({ logs, people, month }: { logs: WorkLog[]; people: Person[]; month: string }) {
  const rows = useMemo(() => people.filter((person) => person.active).map((person) => {
    const personLogs = logs.filter((log) => log.personId === person.id && log.billable);
    const minutes = personLogs.reduce((sum, log) => sum + log.hoursMinutes, 0);
    const weightedPay = personLogs.reduce((sum, log) => sum + log.hoursMinutes * (log.payRateSnapshot ?? 0), 0);
    const rates = new Set(personLogs.map((log) => log.payRateSnapshot ?? 0));
    return { person, minutes, pay: Math.round(weightedPay / 60), rateLabel: rates.size > 1 ? "Mixed rates" : formatRupiah(personLogs[0]?.payRateSnapshot ?? person.hourlyRate ?? 0) };
  }).filter((row) => row.minutes > 0), [logs, people]);

  const totalMinutes = rows.reduce((sum, row) => sum + row.minutes, 0);
  const totalPay = rows.reduce((sum, row) => sum + row.pay, 0);

  return (
    <section>
      <div className="page-title-row"><div><p className="eyebrow">Internal only</p><h1>Salary hours</h1><p>Billable time and estimated hourly pay for {formatMonth(month)}.</p></div></div>
      <div className="salary-summary"><div><span>Total billable hours</span><strong>{formatHours(totalMinutes)}</strong></div><div><span>Estimated pay</span><strong>{formatRupiah(totalPay)}</strong></div></div>
      <div className="card salary-card">
        <div className="table-wrap"><table><thead><tr><th>Person</th><th>Billable entries</th><th className="number-cell">Hours</th><th className="number-cell">Rate used</th><th className="number-cell">Estimated pay</th></tr></thead><tbody>{rows.map((row) => <tr key={row.person.id}><td><span className="person-cell"><span className="avatar">{initials(row.person.name)}</span><span><strong>{row.person.name}</strong><small>{row.person.email}</small></span></span></td><td>{logs.filter((log) => log.personId === row.person.id && log.billable).length}</td><td className="number-cell"><strong>{formatHours(row.minutes)}</strong></td><td className="number-cell">{row.rateLabel}</td><td className="number-cell"><strong>{formatRupiah(row.pay)}</strong></td></tr>)}</tbody></table></div>
        {!rows.length && <EmptyState>No billable entries for {formatMonth(month)}.</EmptyState>}
      </div>
      <p className="fine-print">Estimate only. Each work entry uses the hourly rate stored when that entry was created.</p>
    </section>
  );
}

type SettingsProps = {
  session: Session;
  refresh: () => Promise<void>;
  showToast: (message: string, tone?: "success" | "error") => void;
};

function Settings({ session, refresh, showToast }: SettingsProps) {
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [personName, setPersonName] = useState("");
  const [personEmail, setPersonEmail] = useState("");
  const [personRole, setPersonRole] = useState<Role>("member");
  const [personRate, setPersonRate] = useState("0");
  const [personActive, setPersonActive] = useState(true);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [projectActive, setProjectActive] = useState(true);
  const [projectMemberIds, setProjectMemberIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  function editPerson(person: Person) { setEditingPerson(person); setPersonName(person.name); setPersonEmail(person.email); setPersonRole(person.role); setPersonRate(String(person.hourlyRate ?? 0)); setPersonActive(person.active); }
  function resetPerson() { setEditingPerson(null); setPersonName(""); setPersonEmail(""); setPersonRole("member"); setPersonRate("0"); setPersonActive(true); }
  function editProject(project: Project) { setEditingProject(project); setProjectName(project.name); setClientName(project.clientName); setProjectActive(project.active); setProjectMemberIds(session.projectMembers.filter((item) => item.projectId === project.id).map((item) => item.personId)); }
  function resetProject() { setEditingProject(null); setProjectName(""); setClientName(""); setProjectActive(true); setProjectMemberIds([]); }

  async function savePerson(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const hourlyRate = Math.round(Number(personRate));
      if (editingPerson) await worklogApi.updatePerson(editingPerson.id, { name: personName, role: personRole, hourlyRate, active: personActive });
      else await worklogApi.createPerson({ name: personName, email: personEmail, role: personRole, hourlyRate });
      await refresh(); resetPerson(); showToast(editingPerson ? "Person updated." : "Person added.");
    } catch (caught) { showToast(caught instanceof Error ? caught.message : "Could not save the person.", "error"); }
    finally { setSaving(false); }
  }

  async function saveProject(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingProject) {
        await worklogApi.updateProject(editingProject.id, { name: projectName, clientName, active: projectActive });
        await worklogApi.updateProjectMembers(editingProject.id, projectMemberIds);
      }
      else await worklogApi.createProject({ name: projectName, clientName });
      await refresh(); resetProject(); showToast(editingProject ? "Project updated." : "Project added.");
    } catch (caught) { showToast(caught instanceof Error ? caught.message : "Could not save the project.", "error"); }
    finally { setSaving(false); }
  }

  return (
    <section>
      <div className="page-title-row"><div><p className="eyebrow">Admin</p><h1>Settings</h1><p>Keep the team, project membership, rates, and active projects up to date.</p></div></div>
      <div className="settings-grid">
        <div className="card settings-card"><div className="card-heading"><div><p className="eyebrow">Team</p><h2>People & rates</h2></div></div><form className="settings-form" onSubmit={savePerson}><label><span>Name</span><input value={personName} onChange={(event) => setPersonName(event.target.value)} required /></label><label><span>Email</span><input type="email" value={personEmail} disabled={Boolean(editingPerson)} onChange={(event) => setPersonEmail(event.target.value)} required /></label><div className="split-fields"><label><span>Role</span><select value={personRole} onChange={(event) => setPersonRole(event.target.value as Role)}><option value="member">Member</option><option value="admin">Admin / PM</option></select></label><label><span>Rate (IDR/hour)</span><input type="number" min="0" step="1000" value={personRate} onChange={(event) => setPersonRate(event.target.value)} required /></label></div>{editingPerson && <label className="simple-check"><input type="checkbox" checked={personActive} onChange={(event) => setPersonActive(event.target.checked)} /> Active</label>}<div className="form-actions compact">{editingPerson && <button type="button" className="text-button" onClick={resetPerson}>Cancel</button>}<button className="primary-button" disabled={saving}><Icon name={editingPerson ? "edit" : "plus"} />{editingPerson ? "Save person" : "Add person"}</button></div></form><div className="settings-list">{session.people.map((person) => <button key={person.id} className="settings-row" onClick={() => editPerson(person)}><span className="avatar">{initials(person.name)}</span><span className="settings-main"><strong>{person.name}</strong><small>{person.email} · {person.role === "admin" ? "Admin / PM" : "Member"}</small></span><span className="settings-value">{formatRupiah(person.hourlyRate ?? 0)}<small>/ hour</small></span>{!person.active && <span className="status-pill non-billable">Inactive</span>}<Icon name="edit" /></button>)}</div></div>
        <div className="card settings-card"><div className="card-heading"><div><p className="eyebrow">Work</p><h2>Projects</h2></div></div><form className="settings-form" onSubmit={saveProject}><label><span>Client</span><input value={clientName} onChange={(event) => setClientName(event.target.value)} required /></label><label><span>Project name</span><input value={projectName} onChange={(event) => setProjectName(event.target.value)} required /></label>{editingProject && <><label className="simple-check"><input type="checkbox" checked={projectActive} onChange={(event) => setProjectActive(event.target.checked)} /> Active</label><fieldset className="member-picker"><legend>Project members</legend>{session.people.filter((person) => person.active).map((person) => <label className="simple-check" key={person.id}><input type="checkbox" checked={projectMemberIds.includes(person.id)} onChange={(event) => setProjectMemberIds((current) => event.target.checked ? [...current, person.id] : current.filter((id) => id !== person.id))} />{person.name}</label>)}</fieldset></>}<div className="form-actions compact">{editingProject && <button type="button" className="text-button" onClick={resetProject}>Cancel</button>}<button className="primary-button" disabled={saving}><Icon name={editingProject ? "edit" : "plus"} />{editingProject ? "Save project" : "Add project"}</button></div></form><div className="settings-list">{session.projects.map((project) => <button key={project.id} className="settings-row" onClick={() => editProject(project)}><span className="project-dot" /><span className="settings-main"><strong>{project.name}</strong><small>{project.clientName} · {session.projectMembers.filter((item) => item.projectId === project.id).length} members</small></span>{!project.active && <span className="status-pill non-billable">Inactive</span>}<Icon name="edit" /></button>)}</div></div>
      </div>
    </section>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [logs, setLogs] = useState<WorkLog[]>([]);
  const [view, setView] = useState<View>("dashboard");
  const [month, setMonth] = useState(localMonth());
  const [editing, setEditing] = useState<WorkLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState("");
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, tone: "success" | "error" = "success") => {
    setToast({ message, tone });
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const loadLogs = useCallback(async () => {
    const nextLogs = await worklogApi.logs(month);
    setLogs(nextLogs);
  }, [month]);

  const refreshSession = useCallback(async () => {
    const nextSession = await worklogApi.session();
    setSession(nextSession);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const nextSession = await worklogApi.session();
        const nextLogs = await worklogApi.logs(month);
        if (!cancelled) { setSession(nextSession); setLogs(nextLogs); setFatalError(""); }
      } catch (caught) {
        if (!cancelled) setFatalError(caught instanceof Error ? caught.message : "Could not load the app.");
      } finally { if (!cancelled) setLoading(false); }
    }
    void load();
    return () => { cancelled = true; };
  }, [month]);

  useEffect(() => {
    if (session && session.me.role !== "admin" && view === "dashboard") setView("board");
  }, [session, view]);

  if (loading && !session) return <div className="center-screen"><span className="brand-mark">C1</span><div className="loader" /><p>Loading work log…</p></div>;
  if (fatalError || !session) return <div className="center-screen error-screen"><span className="brand-mark">C1</span><h1>Couldn’t open the work log</h1><p>{fatalError}</p><button className="primary-button" onClick={() => window.location.reload()}>Try again</button></div>;

  const isAdmin = session.me.role === "admin";
  const ownLogs = isAdmin ? logs : logs.filter((log) => log.personId === session.me.id);
  const loggableTasks = isAdmin ? session.tasks : session.tasks.filter((task) => task.assigneeId === session.me.id);
  const navItems: Array<{ id: View; label: string; icon: IconName }> = [
    ...(isAdmin ? [{ id: "dashboard" as View, label: "Dashboard", icon: "dashboard" as IconName }] : []),
    { id: "board", label: "Boards", icon: "board" },
    { id: "log", label: "Log work", icon: "clock" },
    { id: "report", label: "Monthly report", icon: "report" },
    ...(isAdmin ? [{ id: "salary" as View, label: "Salary hours", icon: "salary" as IconName }, { id: "settings" as View, label: "Settings", icon: "settings" as IconName }] : []),
  ];

  return (
    <div className="app-shell">
      <aside className="sidebar no-print">
        <div className="brand-lockup"><span className="brand-mark">C1</span><span><strong>Cloud1</strong><small>Work Log</small></span></div>
        <nav>{navItems.map((item) => <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => { setView(item.id); setEditing(null); }}><Icon name={item.icon} />{item.label}</button>)}</nav>
        <div className="sidebar-spacer" />
        {session.environment !== "production" && <div className="demo-badge"><span />Sample mode</div>}
        <div className="account"><span className="avatar">{initials(session.me.name)}</span><span><strong>{session.me.name}</strong><small>{session.me.email}</small></span></div>
      </aside>
      <main className="main-content">
        <header className="topbar no-print"><div className="mobile-brand"><span className="brand-mark small">C1</span><strong>Work Log</strong></div><MonthPicker value={month} onChange={(next) => next && setMonth(next)} /></header>
        <div className="page-content">
          {view === "dashboard" && isAdmin && <AdminDashboard month={month} />}
          {view === "board" && <Boards session={session} refresh={refreshSession} showToast={showToast} />}
          {view === "log" && <section><div className="page-title-row"><div><p className="eyebrow">{formatMonth(month)}</p><h1>Log work</h1><p>Select an assigned task and record the time spent.</p></div></div><SummaryCards logs={ownLogs} /><WorkForm projects={session.projects} tasks={loggableTasks} editing={editing} onCancelEdit={() => setEditing(null)} onSaved={loadLogs} showToast={showToast} /><WorkList logs={ownLogs} currentPersonId={session.me.id} isAdmin={isAdmin} onEdit={(log) => { setEditing(log); window.scrollTo({ top: 0, behavior: "smooth" }); }} onDeleted={loadLogs} showToast={showToast} /></section>}
          {view === "report" && <MonthlyReport logs={logs} projects={session.projects} month={month} />}
          {view === "salary" && isAdmin && <SalaryReport logs={logs} people={session.people} month={month} />}
          {view === "settings" && isAdmin && <Settings session={session} refresh={refreshSession} showToast={showToast} />}
        </div>
      </main>
      <Toast toast={toast} />
    </div>
  );
}
